import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // This controller manages scroll position tracking for tax organizer questions
  // Handles both simple questions (via focusin) and complex CRUD operations (via rememberFromElement)

  static targets = ["scrollable"]

  connect() {
    // Clean up old accordion-persistence localStorage key (migration)
    localStorage.removeItem('tax-organizer-active-section')

    // Restore scroll position on page load
    this.restoreScrollPosition()

    // Listen for form input focus to save scroll position
    this.boundSaveOnFocus = this.saveOnFocus.bind(this)
    this.element.addEventListener('focusin', this.boundSaveOnFocus)

    // Listen for delete button clicks to save scroll position
    this.boundSaveOnDelete = this.saveOnDelete.bind(this)
    this.element.addEventListener('click', this.boundSaveOnDelete)

    // Listen for turbo stream renders to restore scroll position after DOM updates
    this.boundHandleTurboRender = this.handleTurboRender.bind(this)
    document.addEventListener('turbo:render', this.boundHandleTurboRender)
    document.addEventListener('turbo:frame-render', this.boundHandleTurboRender)
  }

  disconnect() {
    // Clean up event listeners
    this.element.removeEventListener('focusin', this.boundSaveOnFocus)
    this.element.removeEventListener('click', this.boundSaveOnDelete)
    document.removeEventListener('turbo:render', this.boundHandleTurboRender)
    document.removeEventListener('turbo:frame-render', this.boundHandleTurboRender)
  }

  // Save scroll position when user focuses on form inputs
  saveOnFocus(event) {
    const input = event.target

    // Only save for actual form inputs
    if (!['INPUT', 'SELECT', 'TEXTAREA'].includes(input.tagName)) return

    // Find which scrollable target contains this input
    const scrollable = this.scrollableTargets.find(target => target.contains(input))
    if (scrollable) {
      const questionId = scrollable.dataset.questionScrollId
      this.rememberQuestion(questionId)
    }
  }

  // Save scroll position when user clicks delete buttons
  saveOnDelete(event) {
    const deleteButton = event.target.closest('[data-action*="delete"], [data-action*="destroy"], [data-action*="remove"]')
    if (!deleteButton) return

    // Find which scrollable target contains this button
    const scrollable = this.scrollableTargets.find(target => target.contains(deleteButton))
    if (scrollable) {
      const questionId = scrollable.dataset.questionScrollId
      this.rememberQuestion(questionId)
    }
  }

  // Handle turbo render events to restore scroll position after turbo stream updates
  handleTurboRender(event) {
    // Restore scroll position after turbo stream completes
    this.restoreScrollPosition()
  }

  // Called by other controllers (via outlet) when user interacts with a question
  rememberQuestion(questionId) {
    if (questionId) {
      localStorage.setItem('tax_organizer_scroll_position', questionId)
    }
  }

  // Called by other controllers to remember based on DOM element
  rememberFromElement(element) {
    // Find which scrollable target contains this element
    const scrollable = this.scrollableTargets.find(target => target.contains(element))
    if (scrollable) {
      const questionId = scrollable.dataset.questionScrollId
      this.rememberQuestion(questionId)
    }
  }

  // Restore scroll position to the last active question
  restoreScrollPosition() {
    const questionId = localStorage.getItem('tax_organizer_scroll_position')
    if (!questionId) {
      return
    }

    // Find the scrollable target with this question ID
    const scrollable = this.scrollableTargets.find(target => target.dataset.questionScrollId === questionId)
    if (scrollable) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        scrollable.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    } else {
      console.warn('[scroll-position] Scrollable target not found for question:', questionId)
    }
  }

  // Clear stored position (e.g., when leaving the page)
  clear() {
    localStorage.removeItem('tax_organizer_scroll_position')
  }
}
